import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from './Logo';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 48);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navLinks: { id: string; label: string; type: 'scroll' | 'route'; path?: string }[] = [
    { id: 'about', label: 'About', type: 'scroll' },
    { id: 'vision-mission', label: 'Vision & Mission', type: 'scroll' },
    { id: 'services', label: 'Services', type: 'scroll' },
    { id: 'contact', label: 'Contact', type: 'scroll' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <a
            href="#hero"
            onClick={(e) => { e.preventDefault(); navigate('/'); setTimeout(() => scrollTo('hero'), 100); }}
            className="flex items-center gap-3 group cursor-pointer"
          >
            <Logo className="h-6 md:h-7 w-auto" />
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) =>
              link.type === 'route' ? (
                <a
                  key={link.id}
                  href={link.path}
                  onClick={(e) => {
                    e.preventDefault();
                    setMobileOpen(false);
                    navigate(link.path!);
                  }}
                  className={`whitespace-nowrap text-sm font-medium transition-colors duration-200 cursor-pointer ${
                    location.pathname === link.path
                      ? 'text-accent-500'
                      : 'text-foreground-600 hover:text-primary-700'
                  }`}
                >
                  {link.label}
                </a>
              ) : (
                <button
                  key={link.id}
                  onClick={() => scrollTo(link.id)}
                  className="whitespace-nowrap text-sm font-medium transition-colors duration-200 cursor-pointer text-foreground-600 hover:text-primary-700"
                >
                  {link.label}
                </button>
              )
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-9 h-9 flex items-center justify-center cursor-pointer rounded-lg transition-colors text-foreground-700 hover:bg-background-100"
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200/60 px-6 py-4 flex flex-col gap-3">
          {navLinks.map((link) =>
            link.type === 'route' ? (
              <a
                key={link.id}
                href={link.path}
                onClick={(e) => {
                  e.preventDefault();
                  setMobileOpen(false);
                  navigate(link.path!);
                }}
                className={`whitespace-nowrap text-left text-sm font-medium py-2 transition-colors cursor-pointer ${
                  location.pathname === link.path
                    ? 'text-accent-500'
                    : 'text-foreground-700 hover:text-primary-700'
                }`}
              >
                {link.label}
              </a>
            ) : (
              <button
                key={link.id}
                onClick={() => scrollTo(link.id)}
                className="whitespace-nowrap text-left text-sm font-medium text-foreground-700 hover:text-primary-700 py-2 transition-colors cursor-pointer"
              >
                {link.label}
              </button>
            )
          )}
          <button
            onClick={() => scrollTo('contact')}
            className="whitespace-nowrap mt-2 w-full px-5 py-2.5 rounded-full bg-secondary-200 text-foreground-800 text-sm font-semibold hover:bg-secondary-300 transition-all cursor-pointer"
          >
            Let&apos;s build it
            <i className="ri-arrow-right-line ml-1.5"></i>
          </button>
        </div>
      )}
    </nav>
  );
}